import React, { useEffect, useRef, useState } from "react";
import { CloseIcon, SearchIcon } from "../../../Icons";
import StatusBadge, { STATUS_STYLES } from "./StatusBadge";
import PatientAvatar from "./PatientAvatar";
import { getPatients } from "../../../services/patientService";

const patientChipLabel = (p) => {
  const name = [p.lastName, p.firstName].filter(Boolean).join(" ");
  const mrn = p.mrn || "—";
  return `${name || "Patient"} - ${mrn}`;
};

const PatientSearchChip = ({ patient, onClear }) => {
  const style = STATUS_STYLES[patient.status] || STATUS_STYLES.Hold;
  return (
    <div
      className={`inline-flex items-center gap-2 max-w-full min-w-0 h-8 pl-2.5 pr-1 rounded-md border ${style.wrap}`}
    >
      <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${style.dot}`} />
      <span className="text-xs font-semibold truncate min-w-0">
        {patientChipLabel(patient)}
      </span>
      <StatusBadge status={patient.status} compact />
      <button
        type="button"
        onClick={onClear}
        className="inline-flex items-center justify-center h-6 w-6 rounded hover:bg-white/80 text-slate-500 shrink-0"
        aria-label="Clear selected patient and search"
        title="Clear to search"
      >
        <CloseIcon size={14} />
      </button>
    </div>
  );
};

/**
 * Shared patient search bar used on Charts + Episode (detail) pages.
 * Chip shows current patient; clear to search and switch patients.
 */
const PatientSearchToolbar = ({
  patient,
  onSelectPatient,
  onClose,
  closeTitle = "Back to patients",
}) => {
  const [chipActive, setChipActive] = useState(true);
  const [search, setSearch] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const searchWrapRef = useRef(null);
  const searchInputRef = useRef(null);

  useEffect(() => {
    setChipActive(true);
    setSearch("");
    setSearchResults([]);
    setSearchOpen(false);
  }, [patient?.id]);

  useEffect(() => {
    if (chipActive || !search.trim()) {
      setSearchResults([]);
      setSearchLoading(false);
      return undefined;
    }

    setSearchLoading(true);
    const timer = setTimeout(async () => {
      try {
        const res = await getPatients({
          search: search.trim(),
          limit: 8,
          page: 1,
        });
        const result = res.result || res.data || {};
        setSearchResults(result.patients || []);
        setSearchOpen(true);
      } catch {
        setSearchResults([]);
      } finally {
        setSearchLoading(false);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [search, chipActive]);

  useEffect(() => {
    const onPointerDown = (e) => {
      if (!searchWrapRef.current?.contains(e.target)) {
        setSearchOpen(false);
      }
    };
    document.addEventListener("mousedown", onPointerDown);
    return () => document.removeEventListener("mousedown", onPointerDown);
  }, []);

  const clearChipForSearch = () => {
    setChipActive(false);
    setSearch("");
    setSearchResults([]);
    setSearchOpen(false);
    setTimeout(() => searchInputRef.current?.focus(), 0);
  };

  const pickPatient = (patientId) => {
    setSearch("");
    setSearchResults([]);
    setSearchOpen(false);
    setChipActive(true);
    onSelectPatient?.(patientId);
  };

  return (
    <div className="flex items-center gap-2.5">
      <div className="relative flex-1 min-w-0" ref={searchWrapRef}>
        <div className="flex items-center gap-2 h-10 px-3 bg-white border border-slate-200 rounded-lg shadow-sm focus-within:border-blue-400 focus-within:ring-2 focus-within:ring-indigo-100 transition-shadow">
          <SearchIcon size={16} className="text-slate-400 shrink-0" />
          {chipActive && patient ? (
            <PatientSearchChip patient={patient} onClear={clearChipForSearch} />
          ) : (
            <input
              ref={searchInputRef}
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onFocus={() => {
                if (searchResults.length || search.trim()) setSearchOpen(true);
              }}
              placeholder="Search patients by name, MRN, phone..."
              className="flex-1 min-w-0 h-full text-sm text-slate-800 placeholder:text-slate-400 bg-transparent outline-none"
            />
          )}
        </div>

        {searchOpen && !chipActive && search.trim() && (
          <div className="absolute z-40 left-0 right-0 mt-1.5 bg-white border border-slate-200 rounded-lg shadow-lg overflow-hidden">
            {searchLoading ? (
              <p className="px-3 py-3 text-sm text-slate-400">Searching...</p>
            ) : searchResults.length === 0 ? (
              <p className="px-3 py-3 text-sm text-slate-500">
                No matching patients
              </p>
            ) : (
              <ul className="max-h-64 overflow-y-auto divide-y divide-slate-100">
                {searchResults.map((p) => (
                  <li key={p.id}>
                    <button
                      type="button"
                      onClick={() => pickPatient(p.id)}
                      className="w-full flex items-center gap-2.5 px-3 py-2.5 text-left hover:bg-indigo-50 transition-colors"
                    >
                      <PatientAvatar
                        firstName={p.firstName}
                        lastName={p.lastName}
                        size="sm"
                      />
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium text-slate-900 truncate">
                          {[p.lastName, p.firstName].filter(Boolean).join(" ")}
                          {p.mrn ? ` - ${p.mrn}` : ""}
                        </p>
                        {p.phone ? (
                          <p className="text-[11px] text-slate-500 truncate">
                            {p.phone}
                          </p>
                        ) : null}
                      </div>
                      <StatusBadge status={p.status} compact />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>

      {onClose ? (
        <button
          type="button"
          onClick={onClose}
          className="inline-flex items-center justify-center h-10 w-10 shrink-0 bg-white rounded-lg border border-slate-200 shadow-sm text-slate-500 hover:text-slate-800 hover:bg-slate-50 hover:border-slate-300 transition-colors"
          aria-label={closeTitle}
          title={closeTitle}
        >
          <CloseIcon size={18} />
        </button>
      ) : null}
    </div>
  );
};

export default PatientSearchToolbar;
